import React, { Suspense } from 'react'
import clsx from 'clsx'
import classes from './PerformanceComponents.module.css'
import { useMediaQuery } from '@mantine/hooks'
import { Stack, Loader, Group, ActionIcon, Skeleton } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import PerformanceLadder from './PerformanceLadder'
import PerformanceKpiAchievementContribution from './PerformanceKpiAchievementContribution'
import KpiTabsContainer from '../../widgets/components/KpiTabsContainer'
import { WidgetContextProvider } from '../../widgets/providers'
import Tabs from '../../stories/Tabs/Tabs'
import ViewSelector from '../../stories/ViewSelector/ViewSelector'
import { Dropdown } from '../../common/components/dropdown/Dropdown'
import { useSearchParams, useNavigate, useParams } from 'react-router'
import { useAllTeams } from '../../teams/api'
import { useAllPlayers } from '../../players/api'
import { useCompetitionId } from '../../common/hooks'
import { Typography } from '../../common/typography'

const PAGE_SIZE = 20

function toApiSubjectType(subject: 'teams' | 'players'): 'team' | 'user' {
  return subject === 'teams' ? 'team' : 'user'
}

function TeamsLadderPaginationControls({
  page,
  pageSize,
}: {
  page: number
  pageSize: number
}) {
  const [, setSearchParams] = useSearchParams()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const competition_id = useCompetitionId()

  const { data: teamsResponse } = useAllTeams({
    competition_id,
    page,
    page_size: pageSize,
  })

  const total = teamsResponse.total
  const totalPages = Math.ceil(total / pageSize)
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  const handlePrevPage = () => {
    if (page > 1) {
      setSearchParams({ page: (page - 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setSearchParams({ page: (page + 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (total === 0) return null

  return (
    <div
      className={clsx(classes.paginationContainer, {
        [classes.paginationContainerMobile]: !isDesktop,
      })}
    >
      <Typography fs="sm" lh="140">
        {startItem}-{endItem} of {total}
      </Typography>
      <ActionIcon
        variant="default"
        onClick={handlePrevPage}
        disabled={page === 1}
        className={classes.paginationIcon}
      >
        <IconChevronLeft
          data-disabled={page === 1}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>
      <ActionIcon
        variant="default"
        onClick={handleNextPage}
        disabled={page >= totalPages}
        className={classes.paginationIcon}
      >
        <IconChevronRight
          data-disabled={page >= totalPages}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>
    </div>
  )
}

function PlayersLadderPaginationControls({
  page,
  pageSize,
}: {
  page: number
  pageSize: number
}) {
  const [, setSearchParams] = useSearchParams()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const competition_id = useCompetitionId()

  const { data: playersResponse } = useAllPlayers({
    competition_id,
    page,
    page_size: pageSize,
  })

  const total = playersResponse.total
  const totalPages = Math.ceil(total / pageSize)
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  const handlePrevPage = () => {
    if (page > 1) {
      setSearchParams({ page: (page - 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setSearchParams({ page: (page + 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (total === 0) return null

  return (
    <div
      className={clsx(classes.paginationContainer, {
        [classes.paginationContainerMobile]: !isDesktop,
      })}
    >
      <Typography fs="sm" lh="140">
        {startItem}-{endItem} of {total}
      </Typography>
      <ActionIcon
        variant="default"
        onClick={handlePrevPage}
        disabled={page === 1}
        className={classes.paginationIcon}
      >
        <IconChevronLeft
          data-disabled={page === 1}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>
      <ActionIcon
        variant="default"
        onClick={handleNextPage}
        disabled={page >= totalPages}
        className={classes.paginationIcon}
      >
        <IconChevronRight
          data-disabled={page >= totalPages}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>
    </div>
  )
}

export default function PerformanceKpiRank() {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [view, setView] = React.useState<'table' | 'list'>('table')
  const [searchParams] = useSearchParams()
  const currentPage = Number(searchParams.get('page')) || 1
  const { subject } = useParams<{ subject: string }>()
  const subjectType: 'teams' | 'players' =
    subject === 'players' ? 'players' : 'teams'

  return (
    <div
      className={clsx(classes.performance_kpi_rank_container, {
        [classes.performance_kpi_rank_container_mobile]: !isDesktop,
      })}
    >
      <div>
        <KpiTabsContainer title="KPI Rank" id={'analysis_kpi_rank_tabs'}>
          <Suspense
            fallback={
              <Stack w="full" h="400px" justify="center" align="center">
                <Loader color="#0B6B7B" />
              </Stack>
            }
          >
            <SubjectComponentSelect
              subjectType={subjectType}
              view={view}
              onViewChange={setView}
              page={currentPage}
              pageSize={PAGE_SIZE}
            >
              {view === 'table' && (
                <PerformanceLadder page={currentPage} page_size={PAGE_SIZE} />
              )}
              {view === 'list' && <PerformanceKpiAchievementContribution />}
            </SubjectComponentSelect>
          </Suspense>
        </KpiTabsContainer>
      </div>
    </div>
  )
}

function SubjectComponentSelect(props: {
  subjectType: 'teams' | 'players'
  view: 'list' | 'table'
  onViewChange: (value: 'list' | 'table') => void
  page: number
  pageSize: number
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const [componentStatistic, setComponentStatistic] =
    React.useState<string>('percent/value')
  const [component, setComponent] = React.useState<string>('percent')
  const [statistic, setStatistic] = React.useState<string>('value')
  const componentStatistics = [
    { label: 'Target (%)', value: 'percent/value' },
    { label: 'Actual', value: 'actual/value' },
    { label: 'Score', value: 'score/value' },
    { label: 'Rank', value: 'percent/rank' },
  ]
  const isDesktop = useMediaQuery('(min-width: 800px)')

  if (props.subjectType === 'players') {
    componentStatistics.push({ label: 'Team Rank', value: 'percent/team_rank' })
  }

  return (
    <WidgetContextProvider
      context={{
        subject_type: toApiSubjectType(props.subjectType),
        component,
        statistic,
      }}
    >
      <Stack>
        <div
          className={classes.subject_component_select_bar}
          data-mobile={!isDesktop}
        >
          <Tabs
            value={props.subjectType}
            onChange={(value) => {
              if (
                value === 'teams' &&
                componentStatistic === 'percent/team_rank'
              ) {
                setComponentStatistic('percent/rank')
                setComponent('percent')
                setStatistic('rank')
              }
              navigate(`../${value}`)
            }}
            items={[
              { value: 'teams', label: 'Teams' },
              { value: 'players', label: 'Players' },
            ]}
          />
          <Group>
            <ViewSelector
              value={props.view}
              enabled={['table', 'list']}
              onChange={(v) => props.onViewChange(v as 'table' | 'list')}
            />
            <Dropdown
              placeholder="Select component"
              data={componentStatistics}
              disabled={props.view === 'list'}
              styles={{
                root: { maxWidth: '150px' },
                input: { backgroundColor: 'transparent' },
              }}
              value={componentStatistic}
              onChange={(value) => {
                setComponentStatistic(value || 'percent/value')
                const [component, statistic] = value?.split('/') || [
                  'percent',
                  'value',
                ]
                setComponent(component)
                setStatistic(statistic)
              }}
              variant="default"
            />
          </Group>
        </div>
        {props.view === 'table' && (
          <Suspense
            fallback={
              <Skeleton
                height={32}
                width={150}
                style={{ alignSelf: 'flex-end' }}
              />
            }
          >
            {props.subjectType === 'teams' ? (
              <TeamsLadderPaginationControls
                page={props.page}
                pageSize={props.pageSize}
              />
            ) : (
              <PlayersLadderPaginationControls
                page={props.page}
                pageSize={props.pageSize}
              />
            )}
          </Suspense>
        )}
        {props.children}
      </Stack>
    </WidgetContextProvider>
  )
}
